<template lang="pug">
.charts-wrapper(ref="chartWrapper")
  .spinner-container(v-if="loading")
    q-spinner(color="primary", size="3em")
  .chart-generator-wrapper(v-else)
    chart-generator(
      v-for="(chart, index) in charts",
      :key="`${chart.id}_${index}`",
      :chartData="chart",
      :facilityId="facilityId",
      :maxWidth="chartWrapperWidth",
      @delete-chat="deleteChat"
    )
    .spinner-container(v-if="loadingMore")
      q-spinner(color="primary", size="3em")
    div(v-else-if="hasNextPage")
      div(v-observe-visibility="reachedEndOfList")
    .new-chart(@click="addNewChart()") +
</template>

<script setup>
import { ref, computed, onMounted, onBeforeMount, onBeforeUnmount } from "vue";
import ChartGenerator from "../../../shared/chartGenerator.vue";
import backend from "../../../../api";
import { handleError } from "../../../../services/handleErrors";
import { useRouter } from "@/routing/router";

const emit = defineEmits([
  /**
   * Срабатывает при загрузке страницы. Пример параметров: false
   */
  "set-loading",
  /**
   * Срабатывает при получении даты страницы. Нужен для выставление заголовка страницы. Пример параметров: "Лукойл"
   */
  "set-title",
]);

const props = defineProps({
  /* id текущей основной орагнизации выбранной пользователем для дашборда в фильтрах */
  facilityId: { type: Number, default: -1 },
});

const router = useRouter();

const rowsPerPage = 5;

const charts = ref([]);
const page = ref(1);
const loading = ref(true);
const hasNextPage = ref(false);
const loadingMore = ref(false);
const chartWrapper = ref(null);
const chartWrapperWidth = ref(0);

const facilityId = computed(() => props.facilityId);

const setChartWrapperWidth = () => {
  chartWrapperWidth.value = chartWrapper.value.getBoundingClientRect().width;
};

const addNewChart = () => {
  const newChart = {
    id: null,
  };
  charts.value.push(newChart);
};

const getCharts = async () => {
  const params = {
    json: {
      table: {
        page: page.value,
        sortBy: "id",
        descending: false,
        rowsPerPage: rowsPerPage,
        filters: {
          facility_id: facilityId.value,
          chat_type: "dashboard",
        },
        except_filters: {},
        grid: "chats",
      },
    },
  };

  try {
    const { data } = await backend.index("/api/v3/ai/chats/dashboard", { params });
    data.data.forEach(chart => {
      charts.value.push(chart);
    });
    const dataCount = data.count;

    hasNextPage.value = data.count > page.value * rowsPerPage;

    emit("set-title", data.header?.title); // Устанавливаем title дашборда
    page.value++;
  } catch (err) {
    await handleError(err);
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const deleteChat = async id => {
  try {
    loading.value = true;
    await backend.destroy("/api/v3/ai/chats/dashboard", id, {});
    page.value = 1;
    charts.value = [];
    await getCharts();
  } catch (err) {
    await handleError(err);
  }
};

const reachedEndOfList = async reached => {
  if (reached && hasNextPage.value) {
    loadingMore.value = true;
    await getCharts();
  }
};

const refreshCharts = async filters => {
  loading.value = true;

  if (facilityId.value !== filters.facilityId) {
    // Загружаем новую страницу если фасилити изменился
    router.push({ name: "Dashboard", params: { facility_id: filters.facilityId, type: "ai_dashboard" } });
  }
  await getCharts();
};

onBeforeMount(async () => {
  await getCharts();
});

onMounted(() => {
  emit("set-loading", false);
  setChartWrapperWidth();
  window.addEventListener("resize", setChartWrapperWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", setChartWrapperWidth);
});

defineExpose({ refreshCharts });
</script>

<style lang="scss" scoped>
.charts-wrapper {
  min-width: 100%;
}
.chart-generator-wrapper {
  display: flex;
  flex-flow: row wrap;
  gap: 12px;
}
.spinner-container {
  position: relative;
}
.new-chart {
  width: 100%;
  height: 80px;
  border-radius: 32px;
  background-color: var(--ai-dashboard-add-chart-background-color);
  color: var(--ai-dashboard-add-chart-text-color);
  line-height: 80px;
  text-align: center;
  font-size: 28px;
  font-weight: 300;
  cursor: pointer;
  margin-top: 16px;
}
</style>
